import React from "react";
import { makePersistable } from "mobx-persist-store";
import { UIStore } from "./UIStore";
import { NavigationStore } from "./NavigationStore";
import { AuthStore, AUTH_STORE_PERSIST_NAME } from "./AuthStore";
import { AssemblyStore } from "./assemblies/AssemblyStore";
import { RealestateStore } from "./RealestateStore";
import { TicketStore } from "./TicketStore";
import { TicketListStore } from "./tickets/TicketListStore";
import { ContactPersonSearchStore } from "./tickets/ContactPersonSearchStore";
import { DamageNotificationStore } from "./tickets/DamageNotificationStore";
import { NotificationStore } from "./NotificationStore";
import { KeyOrderNotificationStore } from "./tickets/KeyOrderNotificationStore";
import { QuestionToDocumentNotificationStore } from "./tickets/QuestionToDocumentNotificationStore";
import { AssignOrderModalStore } from "./tickets/AssignOrderModalStore";
import { CloseTicketModalStore } from "./tickets/CloseTicketModalStore";
import { ManualEmailModalStore } from "./tickets/ManualEmailModalStore";
import { AssignTicketModalStore } from "./tickets/AssignTicketModalStore";
import { OrderStore } from "./OrderStore";
import { ServicesStore } from "./services/ServicesStore";
import { UserRatingModalStore } from "./UserRatingModalStore";
import { ServicesActivationProgressModalStore } from "./services/modals/ServicesActivationProgressModalStore";
import { ServicesDeactivationProgressModalStore } from "./services/modals/ServicesDeactivationProgressModalStore";
import { ServicesInfoModalStore } from "./services/modals/ServicesInfoModalStore";
import { DocumentStore } from "./DocumentStore";
import { InvoiceStore } from "./krediflow/InvoiceStore";
import { InvoiceListStore } from "./krediflow/InvoiceListStore";
import { CreditorSearchStore } from "./krediflow/CreditorSearchStore";
import { PaymentAccountSearchStore } from "./krediflow/PaymentAccountSearchStore";
import { RealestateSearchStore } from "./RealestateSearchStore";
import { UnitSearchStore } from "./UnitSearchStore";
import { TicketSearchStore } from "./krediflow/TicketSearchStore";
import { OrderSearchStore } from "./krediflow/OrderSearchStore";
import { AssignInvoiceStore } from "./krediflow/AssignInvoiceStore";
import { AccountingRealestateSearchStore } from "./krediflow/AccountingRealestateSearchStore";
import { ApproveRejectInvoiceStore } from "./krediflow/ApproveRejectInvoiceStore";
import { AccountingAccountSearchStore } from "./krediflow/AccountingAccountSearchStore";
import { AccountingStore } from "./krediflow/AccountingStore";
import { StartWorkflowStore } from "./krediflow/StartWorkflowStore";
import { AccountingCostcenterStore } from "./krediflow/AccountingCostcenterSearchStore";
import { RestartCancelInvoiceStore } from "./krediflow/RestartCancelInvoiceStore";
import { AccountingVatStore } from "./krediflow/AccountingVatStore";
import { AssembliesListStore } from "./assemblies/AssembliesListStore";
import { AccountingPayoutAccountStore } from "./krediflow/AccountingPayoutAccountStore";
import { ImportInvoiceFilesStore } from "./krediflow/ImportInvoiceFilesStore";
import { UserSettingsStore } from "./UserSettingsStore";
import { AgendaItemStore } from "./assemblies/AgendaItemStore";
import { SplitPdfStore } from "./krediflow/SplitPdfStore";
import { CommunicationStore } from "./CommunicationStore";
import { CommunicationSettingsStore } from "./CommunicationSettingsStore";
import { AttendanceListStore } from "./assemblies/AttendanceListStore";
import { ProtocolStore } from "./assemblies/ProtocolStore";
import { RealestateVisaStore } from "./krediflow/RealestateVisaStore";
import { UISidebarStore } from "./UISidebarStore";
import { InvoiceFilePreviewStore } from "./krediflow/InvoiceFilePreviewStore";
import { AccountingBookingTextStore } from "./krediflow/AccountingBookingTextStore";
import { FileStore } from "./FileStore";
import { VotingStore } from "./assemblies/VotingStore";
import { AgendaVotingProgressModalStore } from "./assemblies/AgendaVotingProgressModalStore";
import { InvoiceFormStore } from "./krediflow/InvoiceFormStore";
import { AccountingUnitSearchStore } from "./krediflow/AccountingUnitSearchStore";
import { NewsStore } from "./NewsStore";
import { KrediFlowConfigurationStore } from "./KrediFlowConfigurationStore";
import { EnumerationStore } from "src/stores/EnumerationStore";

export class RootStore {
    // General
    uiStore: UIStore;
    uiSidebarStore: UISidebarStore;
    navStore: NavigationStore;
    authStore: AuthStore;
    notificationStore: NotificationStore;
    userRatingModalStore: UserRatingModalStore;
    userSettingsStore: UserSettingsStore;
    fileStore: FileStore;
    communicationSettingsStore: CommunicationSettingsStore;
    enumerationsStore: EnumerationStore;

    // Ticketing
    ticketStore: TicketStore;
    ticketListStore: TicketListStore;
    contactPersonSearchStore: ContactPersonSearchStore;
    damageNotificationStore: DamageNotificationStore;
    keyOrderNotificationStore: KeyOrderNotificationStore;
    questionToDocumentNotificationStore: QuestionToDocumentNotificationStore;
    assignOrderModalStore: AssignOrderModalStore;
    closeTicketModalStore: CloseTicketModalStore;
    assignTicketModalStore: AssignTicketModalStore;
    manualEmailModalStore: ManualEmailModalStore;

    // KrediFlow
    krediFlowConfigurationStore: KrediFlowConfigurationStore;
    invoiceStore: InvoiceStore;
    invoiceListStore: InvoiceListStore;
    creditorSearchStore: CreditorSearchStore;
    paymentAccountSearchStore: PaymentAccountSearchStore;
    ticketSearchStore: TicketSearchStore;
    orderSearchStore: OrderSearchStore;
    assignInvoiceStore: AssignInvoiceStore;
    accountingRealestateSearchStore: AccountingRealestateSearchStore;
    accountingUnitSearchStore: AccountingUnitSearchStore;
    approveRejectInvoiceStore: ApproveRejectInvoiceStore;
    restartCancelInvoiceStore: RestartCancelInvoiceStore;
    accountingAccountSearchStore: AccountingAccountSearchStore;
    accountingStore: AccountingStore;
    accountingCostcenterStore: AccountingCostcenterStore;
    startWorkflowStore: StartWorkflowStore;
    accountingVatStore: AccountingVatStore;
    accountingPayoutAccountStore: AccountingPayoutAccountStore;
    importInvoiceFilesStore: ImportInvoiceFilesStore;
    splitPdfStore: SplitPdfStore;
    invoiceFilePreviewStore: InvoiceFilePreviewStore;
    accountingBookingTextStore: AccountingBookingTextStore;
    realestateVisaStore: RealestateVisaStore;
    invoiceFormStore: InvoiceFormStore;

    // Services / Features
    servicesStore: ServicesStore;
    servicesActivationProgressModalStore: ServicesActivationProgressModalStore;
    servicesDeactivationProgressModalStore: ServicesDeactivationProgressModalStore;
    servicesInfoModalStore: ServicesInfoModalStore;

    // Assemblies
    assemblyStore: AssemblyStore;
    realestateStore: RealestateStore;
    assembliesListStore: AssembliesListStore;
    agendaitemStore: AgendaItemStore;
    communicationStore: CommunicationStore;
    attendanceListStore: AttendanceListStore;
    protocolStore: ProtocolStore;
    votingStore: VotingStore;
    agendaVotingProgressModalStore: AgendaVotingProgressModalStore;

    orderStore: OrderStore;

    documentStore: DocumentStore;

    newsStore: NewsStore;

    realestateSearchStore: RealestateSearchStore;
    unitSearchStore: UnitSearchStore;

    constructor() {
        this.uiStore = new UIStore(this);
        this.uiSidebarStore = new UISidebarStore(this);
        this.navStore = new NavigationStore(this);
        this.authStore = new AuthStore(this);
        this.notificationStore = new NotificationStore(this);
        this.userRatingModalStore = new UserRatingModalStore(this);
        this.userSettingsStore = new UserSettingsStore(this);
        this.fileStore = new FileStore(this);
        this.communicationSettingsStore = new CommunicationSettingsStore(this);
        this.enumerationsStore = new EnumerationStore(this);

        this.ticketStore = new TicketStore(this);
        this.ticketListStore = new TicketListStore(this);
        this.contactPersonSearchStore = new ContactPersonSearchStore(this);
        this.damageNotificationStore = new DamageNotificationStore(this);
        this.keyOrderNotificationStore = new KeyOrderNotificationStore(this);
        this.questionToDocumentNotificationStore = new QuestionToDocumentNotificationStore(this);
        this.assignOrderModalStore = new AssignOrderModalStore(this);
        this.closeTicketModalStore = new CloseTicketModalStore(this);
        this.assignTicketModalStore = new AssignTicketModalStore(this);
        this.manualEmailModalStore = new ManualEmailModalStore(this);

        this.assemblyStore = new AssemblyStore(this);
        this.realestateStore = new RealestateStore(this);
        this.assembliesListStore = new AssembliesListStore(this);
        this.agendaitemStore = new AgendaItemStore(this);
        this.communicationStore = new CommunicationStore(this);
        this.attendanceListStore = new AttendanceListStore(this);
        this.votingStore = new VotingStore(this);
        this.agendaVotingProgressModalStore = new AgendaVotingProgressModalStore(this);

        this.orderStore = new OrderStore(this);

        this.servicesStore = new ServicesStore(this);
        this.servicesActivationProgressModalStore = new ServicesActivationProgressModalStore(this);
        this.servicesDeactivationProgressModalStore = new ServicesDeactivationProgressModalStore(this);
        this.servicesInfoModalStore = new ServicesInfoModalStore(this);

        this.documentStore = new DocumentStore(this);
        this.newsStore = new NewsStore(this);

        this.krediFlowConfigurationStore = new KrediFlowConfigurationStore(this);
        this.invoiceStore = new InvoiceStore(this);
        this.invoiceListStore = new InvoiceListStore(this);
        this.creditorSearchStore = new CreditorSearchStore(this);
        this.paymentAccountSearchStore = new PaymentAccountSearchStore(this);
        this.ticketSearchStore = new TicketSearchStore(this);
        this.orderSearchStore = new OrderSearchStore(this);
        this.assignInvoiceStore = new AssignInvoiceStore(this);
        this.accountingRealestateSearchStore = new AccountingRealestateSearchStore(this);
        this.accountingUnitSearchStore = new AccountingUnitSearchStore(this);
        this.approveRejectInvoiceStore = new ApproveRejectInvoiceStore(this);
        this.restartCancelInvoiceStore = new RestartCancelInvoiceStore(this);
        this.accountingAccountSearchStore = new AccountingAccountSearchStore(this);
        this.accountingCostcenterStore = new AccountingCostcenterStore(this);
        this.accountingStore = new AccountingStore(this);
        this.startWorkflowStore = new StartWorkflowStore(this);
        this.accountingVatStore = new AccountingVatStore(this);
        this.accountingPayoutAccountStore = new AccountingPayoutAccountStore(this);
        this.importInvoiceFilesStore = new ImportInvoiceFilesStore(this);
        this.splitPdfStore = new SplitPdfStore(this);
        this.invoiceFilePreviewStore = new InvoiceFilePreviewStore(this);
        this.accountingBookingTextStore = new AccountingBookingTextStore(this);
        this.realestateVisaStore = new RealestateVisaStore(this);
        this.invoiceFormStore = new InvoiceFormStore(this);

        this.realestateSearchStore = new RealestateSearchStore(this);
        this.unitSearchStore = new UnitSearchStore(this);

        this.protocolStore = new ProtocolStore(this);

        makePersistable(this.authStore, {
            name: AUTH_STORE_PERSIST_NAME,
            properties: [
                "refreshToken",
                "tokenExpirationTimestamp",
                "isLoginProcessRunning",
                "loginCodeVerifier",
                "entryPath",
                "entryUri",
                "loginRedirectUri"
            ],
            storage: window.localStorage
        }).finally(() => this.authStore.setLoaded(true));

        makePersistable(this.uiStore, {
            name: "@portalUiStore",
            properties: ["currentAnnouncement", "wasAnnouncementDownloaded"],
            storage: window.localStorage
        });

        makePersistable(this.invoiceListStore, {
            name: "@invoiceListStore",
            properties: [
                "batchProcessMap",
                "hasBatchProcessMapInitialized",
                "areAllInvoicesOfStateSelected",
                "isBatchProcessing",
                "currentBatchProcessIndex"
            ],
            storage: window.localStorage
        });
    }
}

export const RootStoreContext = React.createContext<RootStore>(undefined!);
